import React, { useState, useContext } from "react";
import { Link } from "react-router-dom";
import omit from "lodash/omit";
import {
  List,
  Select,
  Input,
  TextArea,
  Checkbox,
  Form,
  Icon,
  Radio,
  Confirm,
  Button,
  Message,
} from "semantic-ui-react";
import DatePicker from "react-datepicker";
import { getHashCode } from "utils";
import UserContext from "contexts/UserContext";
import FileDropzone from "./FileDropzone";
import Attachment from "components/Attachment";
import "react-datepicker/dist/react-datepicker.css";

const FIELD_CONTROL_MAP = {
  Select: Select,
  Checkbox: Checkbox,
  Input: Input,
  TextArea: TextArea,
  Radio: Radio,
};

const ReportForm = ({
  report,
  isNew,
  onCancel,
  onSend,
  onSave,
  onDelete,
  canAddAttachments,
  showDateField = true,
}) => {
  const user = useContext(UserContext);
  const [isDirty, setIsDirty] = useState(isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [date, setDate] = useState(report.date);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isSendConfirmOpen, setIsSendConfirmOpen] = useState(false);
  const [files, setFiles] = useState(report.files || []);
  const [fields, setFields] = useState(() =>
    report.fields
      .filter((f) => f)
      .map((f) => {
        const cleanField = omit(f, "__typename");
        cleanField.options = (cleanField.options || []).map((o) =>
          omit(o, "__typename")
        );
        cleanField.value = f.value || "";
        return cleanField;
      })
  );

  const handleDateChange = (date) => {
    setDate(date.toISOString());
    setIsDirty(true);
  };

  const handleRemoveFile = (fileKey) => {
    // TODO: Also delete S3 file
    const newFiles = files.filter((f) => f.key !== fileKey);
    setFiles(newFiles);
    setIsDirty(true);
    //    onSave({files: newFiles});
  };

  const handleChange = (i, value) => {
    const newFields = fields.slice();
    newFields[i] = {
      ...newFields[i],
      value: value,
    };
    setFields(newFields);
    setIsDirty(true);
  };

  const handleCheckboxChange = (name, value, checked) => {
    const newFields = fields.slice();

    const values = newFields[name].value
      ? JSON.parse(newFields[name].value)
      : [];
    if (checked) {
      values.push(value);
    } else {
      const valueIndex = values.indexOf(value);
      if (valueIndex !== -1) {
        values.splice(valueIndex, 1);
      }
    }

    newFields[name] = {
      ...newFields[name],
      value: JSON.stringify(values),
    };

    setFields(newFields);
    setIsDirty(true);
  };

  const handleDropComplete = (fileInput) => {
    console.log("onComplete");
    console.log(fileInput);
    const newFiles = [...files, fileInput];
    setFiles(newFiles);
    onSave({ files: newFiles, fields });
  };

  const Fields = fields.map((f, i) => {
    const fieldProps = {
      ...f,
      name: i,
      control: FIELD_CONTROL_MAP[f.control],
    };
    if (f.control === "Checkbox") {
      const values = f.value ? JSON.parse(f.value) : [];
      return (
        <Form.Field key={`field${i}`}>
          <label>{f.label}</label>
          {f.options.map((o) => (
            <Form.Checkbox
              key={o.text}
              name={o.text}
              label={o.text}
              checked={values.indexOf(o.text) !== -1}
              onChange={(e, { checked }) =>
                handleCheckboxChange(i, o.text, checked)
              }
            />
          ))}
        </Form.Field>
      );
    }
    if (f.control === "Radio") {
      return (
        <Form.Field key={`field${i}`}>
          <label>{f.label}</label>
          {f.options.map((o) => (
            <Form.Radio
              key={o.text}
              label={o.text}
              value={o.text}
              checked={o.text === f.value}
              onChange={(e, { value }) => handleChange(i, value)}
            />
          ))}
        </Form.Field>
      );
    }

    // Any options at this point should be dropdown options
    fieldProps.options = f.options.map((o) => {
      return Object.assign({}, o, {
        key: getHashCode(o.text),
        value: o.text,
      });
    });
    return (
      <Form.Field
        onChange={(e, { value }) => handleChange(i, value)}
        key={`field${i}`}
        {...fieldProps}
      />
    );
  });

  return (
    <Form
      onSubmit={() => {
        setIsSaving(true);
        onSave({ date, fields, files });
        setIsSaving(false);
        setIsDirty(false);
      }}
    >
      {showDateField && (
        <Form.Field width={4}>
          <label>Report Date</label>
          <DatePicker selected={new Date(date)} onChange={handleDateChange} />
        </Form.Field>
      )}
      {fields.length === 0 ? (
        <Message negative>
          <Message.Content>
            <Message.Header>No report questions found</Message.Header>
            This student has no questions selected from your Question Bank.{" "}
            <Link to={`/students/${report.student}/edit/questions`}>
              Update this student's questions
            </Link>{" "}
            before continuing.
          </Message.Content>
        </Message>
      ) : (
        false
      )}
      {Fields}
      {files.length > 0 || canAddAttachments ? (
        <Form.Field>
          <label>
            <Icon name="attach" />
            Attachments
          </label>
          {files.length > 0 ? (
            <List divided>
              {files.map((file) => (
                <Attachment
                  key={file.key}
                  file={file}
                  showClose
                  onClose={() => handleRemoveFile(file.key)}
                />
              ))}
            </List>
          ) : null}
          {canAddAttachments ? (
            <FileDropzone
              studentId={report.student}
              reportId={report.id}
              onComplete={handleDropComplete}
            />
          ) : null}
        </Form.Field>
      ) : null}
      <Form.Group>
        <Form.Button basic type="button" onClick={onCancel}>
          Back
        </Form.Button>
        <Form.Button
          basic
          type="submit"
          primary
          disabled={!isDirty}
          loading={isSaving}
        >
          {isDirty ? "Save" : "Saved"}
        </Form.Button>
        {user.canSendReports !== true ? null : (
          <Form.Button
            title={
              isDirty ? "Changes must be saved before sending" : "Send Report"
            }
            basic
            type="button"
            onClick={() => setIsSendConfirmOpen(true)}
            loading={isSending}
            color="green"
            disabled={isDirty || isSaving}
          >
            Send
          </Form.Button>
        )}
        {user.canEditReports === true && isNew === false ? (
          <Form.Button
            basic
            type="button"
            loading={isDeleting}
            color="red"
            onClick={() => setIsDeleteConfirmOpen(true)}
          >
            Delete
          </Form.Button>
        ) : null}
        <Confirm
          open={isDeleteConfirmOpen}
          onCancel={() => setIsDeleteConfirmOpen(false)}
          onConfirm={async () => {
            setIsDeleting(true);
            await onDelete();
            setIsDeleting(false);
            setIsDeleteConfirmOpen(false);
          }}
          confirmButton={report.reportType === "announcement" ? "Delete Announcement" : "Delete Report"}
          content={
            report.reportType === "announcement"
              ? "Are you sure you want to delete this announcement?"
              : "Are you sure you want to delete this report?"
          }
          header={report.reportType === "announcement" ? "Delete Announcement" : "Delete Report"}
        />
        <Confirm
          open={isSendConfirmOpen}
          header={report.reportType === "announcement" ? "Send Announcement" : "Send Report"}
          content={
            report.reportType === "announcement"
              ? "Once an announcement is sent, it cannot be modified. Send the announcement?"
              : "Once a report is sent, it cannot be modified or deleted. Send the report?"
          }
          size="tiny"
          confirmButton={<Button loading={isSending}>Send</Button>}
          onCancel={() => setIsSendConfirmOpen(false)}
          onConfirm={async () => {
            setIsSending(true);
            await onSend({ date, fields });
            setIsSending(false);
            setIsSendConfirmOpen(false);
          }}
        />
      </Form.Group>
    </Form>
  );
};

export default ReportForm;
